import { Component } from 'react';
import { WithStyles, withStyles } from '@mui/styles';
import classNames from 'classnames';
import { times } from 'lodash';
import {
    Skeleton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow
} from '@mui/material';
import APTableSkeletonLoaderStyle from './styles/APTableSkeleton.style';
import { withTranslate } from '../../../core/TenantProvider';

const SkeletonTableRow = withStyles((theme) => ({
    root: {
        backgroundColor: '#ffffff',
        '&.isResponsive': {
            display: 'block',
            width: 'auto',
            position: 'relative',
            borderBottom: '1px solid rgba(224, 224, 224, 1)',
        }
    },
}))(TableRow);

type ColumnWidthArray = number[];

type Modules = 'notification' | 'referralsSearch';

type ModuleLoaderMapping = {
    columns: number,
    columnWidthArray: ColumnWidthArray,
    customTableCellClass: string,
    responsiveTableCellClass: string,
    customSkeletonContainerClass: string,
    showHeaderCell: boolean
}

const ModuleLoaderMapping: { [key in Modules]: ModuleLoaderMapping } = {
    'referralsSearch': {
        columns: 5,
        columnWidthArray: [240, 160, 160, 100, 250],
        customTableCellClass: '',
        responsiveTableCellClass: 'referralsSearchTableCell',
        customSkeletonContainerClass: '',
        showHeaderCell: true
    },
    'notification': {
        columns: 2,
        columnWidthArray: [],
        customTableCellClass: 'notificationTableCell',
        responsiveTableCellClass: '',
        customSkeletonContainerClass: 'notificationSkeletonContainer',
        showHeaderCell: false
    }
};

interface APTableSkeletonLoaderProps extends WithStyles<typeof APTableSkeletonLoaderStyle> {
    className?: string,
    breakPoint?: number,
    translate: Function,
    skeletonRows: number,
    module: Modules
}

class APTableSkeletonLoader extends Component<APTableSkeletonLoaderProps, {}> {

    render() {
        const { classes,
            breakPoint,
            translate,
            skeletonRows,
            module
        } = this.props;

        const match = window.matchMedia(`(max-width: ${breakPoint ? breakPoint : '840'}px)`);
        const LOADING_RESULTS_LABEL: string = translate('referrals.search.loading.label');
        const enableResponsiveView: boolean = match.matches;

        let tableSkeletonLoader: React.JSX.Element[] = [];
        const {
            columns: currentSkeletonColumns,
            columnWidthArray: currentColumnWidthArray,
            customSkeletonContainerClass: tableSkeletonContainerClass,
            customTableCellClass,
            responsiveTableCellClass,
            showHeaderCell
        } = ModuleLoaderMapping[module] || {};

        const tableCellClass: string = enableResponsiveView && responsiveTableCellClass !== '' ? responsiveTableCellClass : customTableCellClass;

        const tableColumns: React.JSX.Element[] = times(currentSkeletonColumns, (i) => {
            const widthValue: string | number = currentColumnWidthArray && currentColumnWidthArray.length === 0 ? '100%' :
                enableResponsiveView ? currentColumnWidthArray[i] : '100%';

            return (
                <TableCell colSpan={2} className={classNames(classes.tableCell, classes[tableCellClass])} key={`cell-${i}`}>
                    <Skeleton
                        variant="rectangular"
                        width={widthValue}
                        height={30} />
                </TableCell>
            );
        });

        const tableRows: React.JSX.Element[] = times(skeletonRows, (i) => {
            return (
                <SkeletonTableRow key={`row-${i}`} className={classes.customTableRow}>
                    {tableColumns}
                </SkeletonTableRow>
            )
        });

        tableSkeletonLoader.push(
            <div className={classNames(classes.tableSkeletonContainer, classes[tableSkeletonContainerClass])}>
                <TableContainer className={classes.tableContainer}>
                    <Table>
                        {showHeaderCell &&
                            <TableHead>
                                <TableRow>
                                    <TableCell colSpan={9} className={classNames(classes.tableHeadingCell)}>
                                        <span className={classes.searchDataTableHeadingCellSpan}>
                                            {LOADING_RESULTS_LABEL}
                                        </span>
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                        }
                        <TableBody className={classes.tableBody}>
                            {tableRows}
                        </TableBody>
                    </Table>
                </TableContainer>
            </div>
        );

        return (
            <div className={classes.tableSkeleton}>{tableSkeletonLoader}</div>
        );
    }
}

export default withTranslate(withStyles(APTableSkeletonLoaderStyle)(APTableSkeletonLoader));
